import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext';
import MagasinSessionPicker from './components/MagasinSessionPicker';
import { SITE } from './utils/siteConfig';
import { getStaffHomeRoute, isPosOnlyAllowedPath, isPosOnlyUser, isPrimaryAdmin } from './utils/roleRouting';
// ChatGuideWidget removed - unified into ClientChatWidget
import LoginPage from './pages/LoginPage';
import GuyarepairLoginPage from './pages/GuyarepairLoginPage';
import CompanyStorefrontPage from './pages/CompanyStorefrontPage';
import AidePage from './pages/aide/AidePage';
import QrRouter from './pages/QrRouter';
import RepairStatusPage from './pages/public/RepairStatusPage';
import ClientRegisterPage from './pages/ClientRegisterPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import StockPage from './pages/StockPage';
import StockFormPage from './pages/StockFormPage';
import ReparationsPage from './pages/ReparationsPage';
import ReparationFormPage from './pages/ReparationFormPage';
import POSPage from './pages/POSPage';
import PosV2Page from './pages/PosV2Page';
import CommandesPage from './pages/CommandesPage';
import DevisFacturesPage from './pages/DevisFacturesPage';
import AvoirsPage from './pages/AvoirsPage';
import HistoriquePage from './pages/HistoriquePage';
import ActivitePage from './pages/ActivitePage';
import AdminCommissionsPage from './pages/AdminCommissionsPage';
import StaffCommissionsPage from './pages/StaffCommissionsPage';
import AdminPromotionsPage from './pages/AdminPromotionsPage';
import AdminEquipeMagasinsRouter from './pages/AdminEquipeMagasinsRouter';
import PromotionsCalendarPage from './pages/PromotionsCalendarPage';
import ClientPortalPage from './pages/ClientPortalPage';
import ClientCaricaturePage from './pages/ClientCaricaturePage';
import PaiementSuccesPage from './pages/PaiementSuccesPage';
import BoutiqueCommandesPage from './pages/BoutiqueCommandesPage';
import CalculPage from './pages/CalculPage';
import CaisseHistoriquePage from './pages/CaisseHistoriquePage';
import ChatPage from './pages/ChatPage';
import ChatAiDevisPage from './pages/ChatAiDevisPage';
import ClientChatPage from './pages/ClientChatPage';
import PaiementsPage from './pages/PaiementsPage';
import NotesPage from './pages/NotesPage';
import EtiquettesPage from './pages/EtiquettesPage';
import HubPage from './pages/HubPage';
import NotificationsSettingsPage from './pages/NotificationsSettingsPage';
import RemcachesPage from './pages/RemcachesPage';
import CloseCaissePage from './pages/CloseCaissePage';
import OpenCaissePage from './pages/OpenCaissePage';
// import ClientPasswordsPage from './pages/ClientPasswordsPage'; // Phase B2: now an internal tab of ClientsPage
import AdminDatabasePage from './pages/AdminDatabasePage';
import AdminCachePurgeVentesPage from './pages/AdminCachePurgeVentesPage';
import AdminCloudSettingsPage from './pages/AdminCloudSettingsPage';
import AdminIntegrationsPage from './pages/AdminIntegrationsPage';
import StockPhotoAiPage from './pages/StockPhotoAiPage';
import StockCategoryAuditPage from './pages/StockCategoryAuditPage';
import AdminPrinterSettingsPage from './pages/AdminPrinterSettingsPage';
import AdminDownloadsPage from './pages/AdminDownloadsPage';
import PrinterTokensPage from './pages/PrinterTokensPage';
import B2BRegisterPage from './pages/B2BRegisterPage';
import AdminB2BPage from './pages/AdminB2BPage';
import B2BDashboardPage from './pages/B2BDashboardPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import StatsPage from './pages/StatsPage';
import BrandsModelsPage from './pages/BrandsModelsPage';
import B2BPaymentsPage from './pages/B2BPaymentsPage';
import DevisDeplacementPage from './pages/DevisDeplacementPage';
import DevisAcceptPage from './pages/DevisAcceptPage';
import DevisPaiementPage from './pages/DevisPaiementPage';
import DevisPaiementSuccesPage from './pages/DevisPaiementSuccesPage';
import LandingPage from './pages/LandingPage';
import ValidationTraductionsPage from './pages/ValidationTraductionsPage';
import GuyarepairLandingPage from './pages/GuyarepairLandingPage';
import AdminSiteSettingsPage from './pages/AdminSiteSettingsPage';
import AdminCachePage from './pages/AdminCachePage';
import AdminCadbotPrixPage from './pages/AdminCadbotPrixPage';
import AcompteDevisStartPage from './pages/AcompteDevisStartPage';
import AcompteDevisSuccesPage from './pages/AcompteDevisSuccesPage';
import AdminHorsStockPendingPage from './pages/AdminHorsStockPendingPage';
import AdminPertesPage from './pages/AdminPertesPage';
import AdminTodoPage from './pages/AdminTodoPage';
import AdminSupplierCredentialsPage from './pages/AdminSupplierCredentialsPage';
import AdminDemandesFacturesPage from './pages/AdminDemandesFacturesPage';
import LabelConfigPage from './pages/LabelConfigPage';
import DemandeFacturePage from './pages/DemandeFacturePage';
import ReconciliationPage from './pages/ReconciliationPage';
import StockMovementsPage from './pages/StockMovementsPage';
import AuditCompliancePage from './pages/AuditCompliancePage';
import LoyaltyAdminPage from './pages/LoyaltyAdminPage';
import SuperAdminDashboard from './pages/super-admin/SuperAdminDashboard';
import SuperAdminCompanies from './pages/super-admin/SuperAdminCompanies';
import SuperAdminSubscriptions from './pages/super-admin/SuperAdminSubscriptions';
import SuperAdminUsers from './pages/super-admin/SuperAdminUsers';
import SuperAdminStats from './pages/super-admin/SuperAdminStats';
import SuperAdminCommunications from './pages/super-admin/SuperAdminCommunications';
import SuperAdminConfig from './pages/super-admin/SuperAdminConfig';
import SuperAdminIntegrations from './pages/super-admin/SuperAdminIntegrations';
import SuperAdminLogs from './pages/super-admin/SuperAdminLogs';
import SuperAdminImpersonation from './pages/super-admin/SuperAdminImpersonation';
import SuperAdminSecurity from './pages/super-admin/SuperAdminSecurity';
import SuperAdminPackages from './pages/super-admin/SuperAdminPackages';
import SuperAdminAnalytics from './pages/super-admin/SuperAdminAnalytics';
import CompanyAnalyticsPage from './pages/CompanyAnalyticsPage';
import EmailConfigPage from './pages/EmailConfigPage';
import AlertsConfigPage from './pages/AlertsConfigPage';
import PublicShopPage from './pages/PublicShopPage';
import PaymentConfigPage from './pages/PaymentConfigPage';
import SumUpTerminalConfigPage from './pages/SumUpTerminalConfigPage';
import DataImportPage from './pages/DataImportPage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import ThemeSettingsPage from './pages/ThemeSettingsPage';
import DocumentTimezonePage from './pages/DocumentTimezonePage';
import SubscriptionPage from './pages/SubscriptionPage';
import { SubscriptionSuccessPage, SubscriptionCancelPage } from './pages/SubscriptionPages';
import TranslationsPage from './pages/TranslationsPage';
import StockInventoryPage from './pages/StockInventoryPage';
import StockHistoryPage from './pages/StockHistoryPage';
import StockInvoiceImportPage from './pages/StockInvoiceImportPage';
import StockAnalyzePage from './pages/StockAnalyzePage';
import CommandesPiecesPage from './pages/CommandesPiecesPage';
import AllDocumentsPage from './pages/AllDocumentsPage';
import ComptabilitePage from './pages/ComptabilitePage';
import RepairDetailPage from './pages/RepairDetailPage';
import BusinessCardPage from './pages/BusinessCardPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import AnalyticsPage from './pages/AnalyticsPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import FlashOffersPage from './pages/FlashOffersPage';
import ServicePricesPage from './pages/ServicePricesPage';
import TarifsPage from './pages/TarifsPage';
import ImpersonationBanner from './components/ImpersonationBanner';
import TrialBanner from './components/TrialBanner';
import TrialExpiredBlocker from './components/TrialExpiredBlocker';
import PasswordChangeModal from './components/PasswordChangeModal';
import QuickAgent from './components/QuickAgent';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import ServiceWorkerBoot from './components/ServiceWorkerBoot';
import { Toaster } from 'sonner';
import './index.css';

const hasRouteRole = (user, allowedRoles = []) => {
  if (!user || allowedRoles.length === 0) return true;
  if (allowedRoles.includes(user.role)) return true;

  const magasinRole = user.magasin_role || (user.username === 'carlos' ? 'super_admin' : null);
  const allowsAdminArea = allowedRoles.includes('admin');
  if (allowsAdminArea && ['super_admin', 'admin_magasin'].includes(magasinRole)) {
    return true;
  }

  return false;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, isAuthenticated, networkError, retry } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="grid-pattern"></div>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (networkError && !isAuthenticated && localStorage.getItem('token')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-sm">
          <div className="text-red-500 text-5xl mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 11-12.728 0M12 9v4m0 4h.01" /></svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Problème de connexion</h2>
          <p className="text-gray-600 mb-6">Impossible de se connecter au serveur. Vérifiez votre connexion internet.</p>
          <button
            onClick={retry}
            data-testid="network-retry-btn"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Absent / suspension : POS uniquement (pas d'autres pages staff).
  if (isPosOnlyUser(user) && !isPosOnlyAllowedPath(location.pathname)) {
    return <Navigate to="/pos-v2" replace />;
  }

  if (!hasRouteRole(user, allowedRoles)) {
    if (user?.role === 'super_admin') {
      return <Navigate to="/super-admin" replace />;
    }
    if (user?.role === 'client') {
      return <Navigate to="/espace-client" replace />;
    }
    return <Navigate to={getStaffHomeRoute(user)} replace />;
  }

  return children;
};

/** SEC-01 — Routes réservées à l'administrateur principal (username carlos). */
const CarlosOnlyRoute = ({ children }) => {
  const { user, loading, isAuthenticated, networkError, retry } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="grid-pattern"></div>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (networkError && !isAuthenticated && localStorage.getItem('token')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-sm">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Problème de connexion</h2>
          <button onClick={retry} data-testid="network-retry-btn" className="bg-blue-600 text-white px-6 py-3 rounded-lg">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isPrimaryAdmin(user)) {
    return <Navigate to={getStaffHomeRoute(user)} replace />;
  }

  return children;
};

// Client Route - Only for clients
const ClientRoute = ({ children }) => {
  const { user, loading, isAuthenticated, networkError, retry } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="grid-pattern"></div>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (networkError && !isAuthenticated && localStorage.getItem('token')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-sm">
          <div className="text-red-500 text-5xl mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 11-12.728 0M12 9v4m0 4h.01" /></svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Problème de connexion</h2>
          <p className="text-gray-600 mb-6">Impossible de se connecter au serveur. Vérifiez votre connexion internet.</p>
          <button
            onClick={retry}
            data-testid="network-retry-btn-client"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'client') {
    return <Navigate to={getStaffHomeRoute(user)} replace />;
  }

  return children;
};

// Public Route - Redirect if authenticated
const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="grid-pattern"></div>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.role === 'super_admin') {
      return <Navigate to="/super-admin" replace />;
    }
    if (user?.role === 'client') {
      return <Navigate to="/espace-client" replace />;
    }
    if (user?.role === 'b2b') {
      return <Navigate to="/espace-b2b" replace />;
    }
    return <Navigate to={getStaffHomeRoute(user)} replace />;
  }

  return children;
};

// Global back-to-dashboard handler for admin/staff
const BackToDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated || !user || !['admin', 'staff', 'super_admin'].includes(user.role)) return;
    const skipPaths = ['/dashboard', '/', '/login', '/super-admin'];
    if (skipPaths.some(p => location.pathname === p)) return;
    if (location.pathname.startsWith('/super-admin')) return;

    const onPopState = () => {
      window.location.replace('/dashboard');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [location.pathname, user, isAuthenticated]);

  return null;
};

// Landing page redirect — subscribers go to dashboard
const LandingRedirect = () => {
  const { isAuthenticated, user } = useAuth();
  if (isAuthenticated && user) {
    if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
    if (user.role === 'client') return <Navigate to="/espace-client" replace />;
    return <Navigate to={getStaffHomeRoute(user)} replace />;
  }
  return SITE.isRemobiles ? <LandingPage /> : <GuyarepairLandingPage />;
};

// Repair track redirect — when user scans QR code with /track/{ref}
const RepairTrackRedirect = () => {
  const params = window.location.pathname.split('/track/')[1];
  // Redirect to remobiles landing with the ref pre-filled
  return <Navigate to={`/remobiles?ref=${params || ''}`} replace />;
};

// ChatGuideController removed - AI guide now in ClientChatWidget


// Phase 2 §2.9 — show modal when must_change_password flag is set
const MustChangePasswordGate = () => {
  const { user, refreshUser } = useAuth();
  if (!user || !user.must_change_password) return null;
  return (
    <PasswordChangeModal
      isClient={user.role === 'client'}
      reason={user.password_change_reason}
      onSuccess={() => { refreshUser && refreshUser(); }}
    />
  );
};


function MagasinPickerOverlay() {
  const { needsMagasinPick } = usePermissions();
  const { user } = useAuth();
  if (!user || !needsMagasinPick) return null;
  return <MagasinSessionPicker />;
}

function App() {
  return (
    <LanguageProvider>
    <ThemeProvider>
    <AuthProvider>
    <PermissionsProvider>
      <Router>
        <Toaster position="top-right" richColors closeButton />
        <MagasinPickerOverlay />
        <MustChangePasswordGate />
        <ImpersonationBanner />
        {!SITE.isRemobiles && <TrialBanner />}
        {!SITE.isRemobiles && <TrialExpiredBlocker />}
        <BackToDashboard />
        <ServiceWorkerBoot />
        <QuickAgent />
        <PwaInstallPrompt />
        <Routes>
          {/* Landing Page */}
          {/* Landing page — redirect logged-in users to dashboard */}
          <Route path="/" element={<LandingRedirect />} />
          <Route path="/caricature" element={<LandingRedirect />} />

          {/* Legacy SaaS login route - disabled in Remobiles local/desktop mode */}
          <Route
            path="/guyarepair/connexion"
            element={
              SITE.isRemobiles
                ? <Navigate to="/login/staff" replace />
                : <PublicRoute><GuyarepairLoginPage /></PublicRoute>
            }
          />

          {/* R.E.Mobiles Landing Page */}
          <Route path="/remobiles" element={<LandingPage />} />

          {/* Public repair tracking redirect */}
          <Route path="/track/:ref" element={<RepairTrackRedirect />} />

          {/* Company Storefront (public) */}
          <Route path="/r/:slug" element={<QrRouter />} />
          <Route path="/suivi/:ref" element={<RepairStatusPage />} />
          <Route path="/aide" element={<AidePage />} />

          {/* Separate Login Routes */}
          <Route path="/login/staff" element={<PublicRoute><LoginPage defaultTab="admin" /></PublicRoute>} />
          <Route path="/login/client" element={<PublicRoute><LoginPage defaultTab="client" /></PublicRoute>} />
          <Route path="/login/entreprise" element={<PublicRoute><LoginPage defaultTab="entreprise" /></PublicRoute>} />

          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/inscription-client" 
            element={
              <PublicRoute>
                <ClientRegisterPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/inscription-b2b" 
            element={
              <PublicRoute>
                <B2BRegisterPage />
              </PublicRoute>
            } 
          />

          {/* Client Portal Route */}
          <Route 
            path="/espace-client" 
            element={
              <ClientRoute>
                <ClientPortalPage />
              </ClientRoute>
            } 
          />

          <Route
            path="/espace-client/caricature"
            element={
              <ClientRoute>
                <ClientCaricaturePage />
              </ClientRoute>
            }
          />

          <Route 
            path="/paiement-succes" 
            element={
              <ClientRoute>
                <PaiementSuccesPage />
              </ClientRoute>
            } 
          />

          {/* Staff/Admin Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/analytics" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <CompanyAnalyticsPage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/clients" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <ClientsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/stock" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StockPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/stock/ajouter" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StockFormPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/stock/modifier/:id" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StockFormPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/stock/inventaire" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StockInventoryPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/stock/historique" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StockHistoryPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/stock/commandes-pieces" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <CommandesPiecesPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/stock/import-facture" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StockInvoiceImportPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/stock/analyser" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StockAnalyzePage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/reparations" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <ReparationsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/reparations/nouveau" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <ReparationFormPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/reparations/modifier/:ref" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <ReparationFormPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/reparations/:ref" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <RepairDetailPage />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/pos"
            element={<Navigate to="/pos-v2" replace />}
          />

          <Route
            path="/pos-v2"
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <PosV2Page />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/commandes" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <CommandesPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/devis-factures" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <DevisFacturesPage />
              </ProtectedRoute>
            } 
          />

          
          <Route
            path="/avoirs"
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <AvoirsPage />
              </ProtectedRoute>
            }
          />

<Route 
            path="/devis-deplacement" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <DevisDeplacementPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/documents" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AllDocumentsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/comptabilite" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ComptabilitePage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/cartes-visite" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <BusinessCardPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/calcul" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <CalculPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/site-settings" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteSettingsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/company-settings" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <CompanySettingsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/theme" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ThemeSettingsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/horaires-documents" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DocumentTimezonePage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/subscription" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SubscriptionPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/activite" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <ActivitePage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/historique-ventes" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <HistoriquePage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/historique" 
            element={<Navigate to="/activite?tab=ventes" replace />}
          />

          <Route 
            path="/caisse-historique" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <CaisseHistoriquePage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/ouvrir-caisse" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien', 'commercial']}>
                <OpenCaissePage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/fermer-caisse" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien', 'commercial']}>
                <CloseCaissePage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/chat" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <ChatPage />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/chat/devis-ia"
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <ChatAiDevisPage />
              </ProtectedRoute>
            }
          />

          <Route path="/chat-clients" element={<Navigate to="/chat" replace />} />

          <Route 
            path="/notes" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <NotesPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/etiquettes" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <EtiquettesPage />
              </ProtectedRoute>
            } 
          />

          {/* Phase 5.2 (2026-05-15) — Hub tickets / factures / devis */}
          <Route
            path="/hub"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin', 'staff', 'employe', 'technicien']}>
                <HubPage />
              </ProtectedRoute>
            }
          />

          {/* Phase 5.3 (2026-05-15) — Push notifications settings (admin/staff) */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin', 'staff', 'employe', 'technicien', 'user']}>
                <NotificationsSettingsPage />
              </ProtectedRoute>
            }
          />

          <Route 
            path="/etiquettes/config" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <LabelConfigPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/utilisateurs" 
            element={<Navigate to="/admin/equipe-magasins" replace />}
          />

          <Route 
            path="/promotions" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <PromotionsCalendarPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/promotions" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPromotionsPage />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/admin/magasins"
            element={<Navigate to="/admin/equipe-magasins?tab=magasins" replace />}
          />

          {/* 2026-05 — Page unifiée Équipe & Magasins (canonical) */}
          <Route
            path="/admin/equipe-magasins"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminEquipeMagasinsRouter />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users"
            element={<Navigate to="/admin/equipe-magasins" replace />}
          />

          <Route 
            path="/admin/commissions" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCommissionsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/mes-commissions" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien', 'staff', 'commercial']}>
                <StaffCommissionsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/settings/cloud" 
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminCloudSettingsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/settings/printer" 
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <AdminPrinterSettingsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/installateurs" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminDownloadsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/downloads" 
            element={<Navigate to="/admin/installateurs" replace />} 
          />

          <Route 
            path="/admin/printer-tokens" 
            element={
              <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                <PrinterTokensPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/database" 
            element={
              <CarlosOnlyRoute>
                <AdminDatabasePage />
              </CarlosOnlyRoute>
            } 
          />

          <Route
            path="/admin/cache-purge-ventes"
            element={
              <CarlosOnlyRoute>
                <AdminCachePurgeVentesPage />
              </CarlosOnlyRoute>
            }
          />

          <Route 
            path="/admin/integration-apis" 
            element={
              <CarlosOnlyRoute>
                <AdminIntegrationsPage />
              </CarlosOnlyRoute>
            } 
          />

          <Route 
            path="/admin/cles-api" 
            element={
              <CarlosOnlyRoute>
                <AdminIntegrationsPage />
              </CarlosOnlyRoute>
            } 
          />

          <Route
            path="/admin/stock-photos-ai"
            element={
              <CarlosOnlyRoute>
                <StockPhotoAiPage />
              </CarlosOnlyRoute>
            }
          />

          <Route
            path="/admin/stock-category-audit"
            element={
              <CarlosOnlyRoute>
                <StockCategoryAuditPage />
              </CarlosOnlyRoute>
            }
          />

          <Route 
            path="/admin/b2b" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <AdminB2BPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/marques-modeles" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <BrandsModelsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/b2b/paiements" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user', 'technicien']}>
                <B2BPaymentsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/cache-fournisseurs" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCachePage />

              </ProtectedRoute>
            } 
          />

          <Route
            path="/admin/cadbot-prix"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCadbotPrixPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/a-faire"
            element={
              <CarlosOnlyRoute>
                <AdminTodoPage />
              </CarlosOnlyRoute>
            }
          />

          <Route
            path="/admin/pertes"
            element={
              <CarlosOnlyRoute>
                <AdminPertesPage />
              </CarlosOnlyRoute>
            }
          />

          <Route
            path="/admin/ventes-hors-stock"
            element={
              <CarlosOnlyRoute>
                <AdminHorsStockPendingPage />
              </CarlosOnlyRoute>
            }
          />

          <Route 
            path="/admin/identifiants-fournisseurs" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSupplierCredentialsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/payment-config" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PaymentConfigPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/sumup-terminal" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SumUpTerminalConfigPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/import-data" 
            element={
              <CarlosOnlyRoute>
                <DataImportPage />
              </CarlosOnlyRoute>
            } 
          />

          <Route 
            path="/admin/email-config" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <EmailConfigPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/alerts-config" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AlertsConfigPage />
              </ProtectedRoute>
            } 
          />


          <Route 
            path="/admin/demandes-factures" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDemandesFacturesPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/reconciliation" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ReconciliationPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/stock-avance" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StockMovementsPage />
              </ProtectedRoute>
            } 
          />

          {/* STEP 3.5 multi-magasin — public alias of the stock-movements page */}
          <Route 
            path="/stock/mouvements" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StockMovementsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/audit" 
            element={
              <CarlosOnlyRoute>
                <AuditCompliancePage />
              </CarlosOnlyRoute>
            } 
          />

          <Route 
            path="/admin/fidelite" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <LoyaltyAdminPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/offres-flash" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'technicien']}>
                <FlashOffersPage />
              </ProtectedRoute>
            } 
          />


          <Route 
            path="/admin/translations" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <TranslationsPage />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/admin/validation-traductions"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <ValidationTraductionsPage />
              </ProtectedRoute>
            }
          />

          <Route 
            path="/tarifs-gestion" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <ServicePricesPage />
              </ProtectedRoute>
            } 
          />

          {/* Public tarifs page - no auth */}
          <Route path="/tarifs" element={<TarifsPage />} />

          {/* B2B Portal Route */}
          <Route 
            path="/espace-b2b" 
            element={
              <ProtectedRoute allowedRoles={['b2b']}>
                <B2BDashboardPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/clients/mots-de-passe" 
            element={<Navigate to="/clients?tab=pins" replace />}
          />

          <Route 
            path="/boutique/commandes" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <BoutiqueCommandesPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/etiquettes" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StockPage />
              </ProtectedRoute>
            } 
          />

          {/* Password Reset Route */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/mot-de-passe-oublie" element={<ResetPasswordPage />} />

          {/* Email Verification Route */}
          <Route path="/verifier-email" element={<EmailVerificationPage />} />

          {/* Statistics */}
          <Route 
            path="/stats" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StatsPage />
              </ProtectedRoute>
            } 
          />

          {/* Hidden setup page - no auth required */}
          <Route path="/remcaches" element={<RemcachesPage />} />

          {/* Public invoice request page - no auth required */}
          <Route path="/demande-facture" element={<DemandeFacturePage />} />

          {/* Public devis pages - no auth required */}
          <Route path="/devis/accepter/:devisId" element={<DevisAcceptPage />} />
          <Route path="/devis/paiement/:devisId" element={<DevisPaiementPage />} />
          <Route path="/devis/paiement-succes/:devisId" element={<DevisPaiementSuccesPage />} />

          {/* Super Admin Routes */}
          <Route path="/super-admin" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminDashboard /></ProtectedRoute>} />
          <Route path="/super-admin/analytics" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminAnalytics /></ProtectedRoute>} />
          <Route path="/super-admin/companies" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminCompanies /></ProtectedRoute>} />
          <Route path="/super-admin/subscriptions" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminSubscriptions /></ProtectedRoute>} />
          <Route path="/super-admin/users" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminUsers /></ProtectedRoute>} />
          <Route path="/super-admin/stats" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminStats /></ProtectedRoute>} />
          <Route path="/super-admin/communications" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminCommunications /></ProtectedRoute>} />
          <Route path="/super-admin/config" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminConfig /></ProtectedRoute>} />
          <Route path="/super-admin/integrations" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminIntegrations /></ProtectedRoute>} />
          <Route path="/super-admin/logs" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminLogs /></ProtectedRoute>} />
          <Route path="/super-admin/impersonation" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminImpersonation /></ProtectedRoute>} />
          <Route path="/super-admin/security" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminSecurity /></ProtectedRoute>} />
          <Route path="/super-admin/packages" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminPackages /></ProtectedRoute>} />

          {/* Public shop pages */}
          <Route path="/shop/:shopId" element={<PublicShopPage />} />

          {/* MAILS AI retiré LOT-UI-01 — redirect legacy URLs */}
          {SITE.isRemobiles && (
            <Route path="/mails-ai" element={<Navigate to="/dashboard" replace />} />
          )}

          {SITE.isRemobiles && (
            <Route 
              path="/site-analytics" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AnalyticsPage />
                </ProtectedRoute>
              } 
            />
          )}

          {/* Public legal pages */}
          <Route path="/politique-confidentialite" element={<PrivacyPolicyPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/conditions-utilisation" element={<TermsOfServicePage />} />
          <Route path="/cgu" element={<TermsOfServicePage />} />
          <Route path="/carte" element={<BusinessCardPage />} />

          {/* Subscription pages */}
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/acompte-devis-succes" element={<AcompteDevisSuccesPage />} />
          <Route path="/acompte-devis/:id" element={<AcompteDevisStartPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </PermissionsProvider>
    </AuthProvider>
    </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
